import { useState, useEffect, useCallback } from 'react'
import { NativePurchases, PURCHASE_TYPE, type Product } from '@capgo/native-purchases'

// ─── Produits IAP déclarés dans App Store Connect ─────────────────────────────
// ⚠️  Ces identifiants doivent correspondre EXACTEMENT aux Product IDs
//     créés dans App Store Connect → votre app → In-App Purchases.
//     Tous doivent être de type "Consumable" (consommable, non-renouvelable).

export const IAP_PRODUCT_IDS = [
  'selah_donation_deux',
  'selah_donation_cinq',
  'selah_donation_dix',
  'selah_donation_vingt',
  'selah_donation_cinquante',
] as const

export type IAPProductId = (typeof IAP_PRODUCT_IDS)[number]

// Labels de repli affichés si les produits n'ont pas encore été chargés
// (ex : première ouverture hors-ligne ou produits pas encore approuvés Apple)
const FALLBACK_LABEL: Record<IAPProductId, string> = {
  'selah_donation_deux':       '2 €',
  'selah_donation_cinq':       '5 €',
  'selah_donation_dix':        '10 €',
  'selah_donation_vingt':      '20 €',
  'selah_donation_cinquante':  '50 €',
}

// ─── Messages d'erreur francisés ──────────────────────────────────────────────

function friendlyIAPError(raw: string): string {
  const s = raw.toLowerCase()
  if (s.includes('cancel') || s.includes('e_user_cancelled') || s.includes('cancelled'))
    return '' // annulation volontaire → pas de message
  if (s.includes('network') || s.includes('internet') || s.includes('connection'))
    return 'Vérifiez votre connexion internet et réessayez.'
  if (s.includes('not available') || s.includes('unavailable') || s.includes('not allowed'))
    return 'Les achats intégrés ne sont pas disponibles sur cet appareil.'
  if (s.includes('already') || s.includes('already owned'))
    return 'Vous avez déjà effectué ce don. Merci pour votre soutien !'
  if (s.includes('payment') || s.includes('declined'))
    return 'Paiement refusé. Vérifiez votre compte Apple et réessayez.'
  return 'Achat impossible. Veuillez réessayer.'
}

// ─── Type exporté ─────────────────────────────────────────────────────────────

export type IAPProduct = Product & {
  /** true si le produit provient du fallback local (prix non encore chargé depuis l'App Store) */
  isFallback?: boolean
}

// ─── Helper : produit de repli ────────────────────────────────────────────────

function makeFallback(id: IAPProductId): IAPProduct {
  return {
    identifier:                  id,
    title:                       FALLBACK_LABEL[id],
    description:                 '',
    price:                       0,
    priceString:                 FALLBACK_LABEL[id],
    currencyCode:                'EUR',
    currencySymbol:              '€',
    isFamilyShareable:           false,
    subscriptionGroupIdentifier: '',
    subscriptionPeriod:          null as unknown as Product['subscriptionPeriod'],
    introductoryPrice:           null,
    discounts:                   [],
    isFallback:                  true,
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useInAppPurchase
 *
 * Charge les produits IAP depuis le Store au montage et expose
 * une fonction `purchase(productId)` pour déclencher un achat.
 *
 * Prérequis :
 *  - Les produits doivent être créés dans App Store Connect (type Consumable)
 *  - La capability "In-App Purchases" doit être activée dans Xcode
 *    (Signing & Capabilities → + Capability → In-App Purchase)
 */
export function useInAppPurchase() {
  const [products,        setProducts]        = useState<IAPProduct[]>([])
  const [productsLoading, setProductsLoading] = useState(true)
  const [productsError,   setProductsError]   = useState<string | null>(null)
  const [purchasing,      setPurchasing]      = useState(false)
  const [purchaseError,   setPurchaseError]   = useState<string | null>(null)
  const [purchaseSuccess, setPurchaseSuccess] = useState(false)

  // ── Chargement des produits au montage ────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    setProductsLoading(true)
    setProductsError(null)

    NativePurchases.getProducts({
      productIdentifiers: [...IAP_PRODUCT_IDS],
      productType:        PURCHASE_TYPE.INAPP,
    })
      .then(({ products: storeProducts }) => {
        if (cancelled) return

        if (!storeProducts?.length) {
          // Produits pas encore disponibles — afficher les replis locaux
          setProducts(IAP_PRODUCT_IDS.map(makeFallback))
        } else {
          // Conserver l'ordre déclaré dans IAP_PRODUCT_IDS
          const ordered = IAP_PRODUCT_IDS
            .map(id => storeProducts.find(p => p.identifier === id) ?? makeFallback(id))
          setProducts(ordered)
        }
      })
      .catch(err => {
        if (cancelled) return
        if (!import.meta.env.PROD) console.warn('[IAP] getProducts error :', err)
        setProductsError('Impossible de charger les produits. Réessayez.')
        setProducts(IAP_PRODUCT_IDS.map(makeFallback))
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false)
      })

    return () => { cancelled = true }
  }, [])

  // ── Achat ─────────────────────────────────────────────────────────────────
  const purchase = useCallback(async (productId: string) => {
    setPurchaseError(null)
    setPurchaseSuccess(false)
    setPurchasing(true)

    try {
      await NativePurchases.purchaseProduct({
        productIdentifier: productId,
        productType:       PURCHASE_TYPE.INAPP,
      })
      setPurchaseSuccess(true)
    } catch (err) {
      const msg      = (err as Error)?.message ?? ''
      const friendly = friendlyIAPError(msg)
      if (friendly) setPurchaseError(friendly)
      // annulation silencieuse → friendly === ''
    } finally {
      setPurchasing(false)
    }
  }, [])

  return {
    products,
    productsLoading,
    productsError,
    purchase,
    purchasing,
    purchaseError,
    purchaseSuccess,
    clearPurchaseError:    () => setPurchaseError(null),
    resetPurchaseSuccess:  () => setPurchaseSuccess(false),
  }
}
